/**
 * Simple state manager for collapsible category sections
 * Manages expanded/collapsed state for legend categories
 */
export class CategoryStateManager {
  private state = new Map<string, boolean>();

  /**
   * Toggle the expanded state of a category
   * @param key - The category translation key
   * @returns The new state (true if expanded, false if collapsed)
   */
  toggle(key: string): boolean {
    const currentState = this.state.get(key) ?? true; // Default to expanded
    const newState = !currentState;
    this.state.set(key, newState);
    return newState;
  }

  /**
   * Check if a category is expanded
   * @param key - The category translation key
   * @param defaultValue - Default value if not set (default: true)
   * @returns True if expanded, false if collapsed
   */
  isExpanded(key: string, defaultValue: boolean = true): boolean {
    return this.state.get(key) ?? defaultValue;
  }

  /**
   * Explicitly set the expanded state of a category
   * @param key - The category translation key
   * @param value - True to expand, false to collapse
   */
  setExpanded(key: string, value: boolean): void {
    this.state.set(key, value);
  }

  /**
   * Clear all state
   */
  clear(): void {
    this.state.clear();
  }

  /**
   * Get all category keys that are currently tracked
   */
  getTrackedKeys(): string[] {
    return Array.from(this.state.keys());
  }

  /**
   * Set all categories to expanded state
   * @param keys - Array of category keys to expand
   */
  expandAll(keys: string[]): void {
    keys.forEach(key => this.state.set(key, true));
  }

  /**
   * Set all categories to collapsed state
   * @param keys - Array of category keys to collapse
   */
  collapseAll(keys: string[]): void {
    keys.forEach(key => this.state.set(key, false));
  }

  /**
   * Check if all categories are expanded
   * @param keys - Array of category keys to check
   * @returns True if all are expanded
   */
  areAllExpanded(keys: string[]): boolean {
    return keys.every(key => this.isExpanded(key));
  }
}
